import { useMutation } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import { downloadFileNoPopup } from "helpers/linkHelper";
import { exportReport } from "../api/evaluation-report-api";
import type {
	ReportExportPayload,
	ReportExportResponse,
} from "../api/evaluation-report-api";

/**
 * Mutation hook for exporting the final report (sync v1 — may take several
 * seconds). On success it downloads the server-provided `download_url` (never a
 * client-constructed URL). Errors are surfaced via the mutation's
 * `isError`/`error` for ReportPanel's retry banner.
 *
 * This used to call `window.open(url, "_blank")`, which browsers blocked as a
 * popup: the export takes seconds, so by the time `onSuccess` fires the click
 * that started it is no longer an active user gesture, and only a gesture buys
 * you a new tab. Nothing was wrong with the URL — the tab was simply refused.
 *
 * `downloadFileNoPopup` clicks a hidden anchor instead, which is not subject to
 * the popup blocker. Passing `newWindow = false` matters: `download_url` is a
 * cross-origin S3 presigned URL, and the `download` attribute is ignored
 * cross-origin, so a `_blank` anchor would degrade into the same blockable
 * new-tab navigation. Same shape as PdfSettings' export.
 */
export const useExportReport = (
	rfxId: number | undefined,
	evalId: number | undefined
): UseMutationResult<ReportExportResponse, Error, ReportExportPayload> => {
	return useMutation({
		mutationFn: (payload: ReportExportPayload) =>
			exportReport(rfxId!, evalId!, payload),
		onSuccess: (data) => {
			if (data?.download_url) {
				downloadFileNoPopup(data.download_url, data.filename, false);
			}
		},
	});
};
